/**
 * 테스트 회사(데모 모드) 배너 — 토스 스타일
 *
 * - 정상: 3개 메시지 롤링 (사용량 → 회사 연결 → 웹 플랜)
 * - 한도 도달: 경고 배너 (닫기 불가)
 * - fade-slide 뉴스 티커 애니메이션
 */
import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  MY_COMPANIES,
  DEMO_STATUS,
  type MyCompaniesData,
  type DemoStatusData,
} from "@/lib/graphql/user";
import { WEB_URL } from "@/lib/constants/app";
import { openExternalUrl } from "@/lib/utils/browser";

export function TestCompanyBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { data: companiesData } = useQuery<MyCompaniesData>(MY_COMPANIES, {
    skip: !user?.companyId,
    fetchPolicy: "cache-first",
  });

  const currentCompany = companiesData?.myCompanies?.find(
    (c) => c.companyId === user?.companyId && c.status === "approved"
  );

  const isTestCompany = !!currentCompany?.isTest;

  const { data: demoData } = useQuery<DemoStatusData>(DEMO_STATUS, {
    skip: !isTestCompany,
    fetchPolicy: "cache-and-network",
  });

  const demoStatus = demoData?.demoStatus ?? null;

  // 3개 메시지 롤링 (4초 간격)
  useEffect(() => {
    if (!isTestCompany || dismissed || demoStatus?.isProjectLimitReached) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % 3);
        setIsTransitioning(false);
      }, 250);
    }, 4000);

    return () => clearInterval(interval);
  }, [isTestCompany, dismissed, demoStatus?.isProjectLimitReached]);

  const handleOpenWeb = useCallback(async () => {
    await openExternalUrl(`${WEB_URL}/settings`);
  }, []);

  if (!isTestCompany) {
    return null;
  }

  // 한도 도달: 경고 배너
  if (demoStatus?.isProjectLimitReached) {
    return (
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{
          background: "#FEF2F2",
          borderBottom: "1px solid rgba(239, 68, 68, 0.1)",
        }}
      >
        <div className="flex items-center gap-2.5 text-[13px]" style={{ color: "#DC2626" }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0"
          >
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span style={{ fontWeight: 500 }}>
            한도에 도달했어요{" "}
            <button
              type="button"
              onClick={() => navigate("/settings")}
              style={{ fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
            >
              회사 연결하기
            </button>
          </span>
        </div>
      </div>
    );
  }

  if (dismissed) {
    return null;
  }

  const showUsage = demoStatus != null;

  const messages: React.ReactNode[] = [
    showUsage ? (
      <span key="usage" style={{ fontWeight: 500 }}>
        데모 모드
        <span style={{ margin: "0 6px", opacity: 0.35 }}>·</span>
        <span className="tabular-nums">
          프로젝트 {demoStatus.projectCount}/{demoStatus.projectLimit}
        </span>
      </span>
    ) : (
      <span key="demo" style={{ fontWeight: 500 }}>
        데모 모드로 사용 중이에요
      </span>
    ),
    <span key="company" style={{ fontWeight: 400 }}>
      소속 회사가 있나요?{" "}
      <button
        type="button"
        onClick={() => navigate("/settings")}
        style={{ fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
      >
        연결하기
      </button>
    </span>,
    <span key="plan" style={{ fontWeight: 400 }}>
      더 많은 기능이 필요하다면{" "}
      <button
        type="button"
        onClick={handleOpenWeb}
        style={{ fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
      >
        플랜 등록
      </button>
    </span>,
  ];

  return (
    <div
      className="flex items-center justify-between px-4 py-2.5"
      style={{
        background: "#FFFBEB",
        borderBottom: "1px solid rgba(245, 158, 11, 0.1)",
      }}
    >
      <div className="flex items-center gap-2.5 min-w-0 text-[13px]" style={{ color: "#92400E" }}>
        {/* 아이콘 — 작고 조용하게 */}
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "6px",
            background: "rgba(245, 158, 11, 0.12)",
            color: "#F59E0B",
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        </div>

        {/* 롤링 텍스트 */}
        <div className="relative h-5 overflow-hidden">
          <div
            className="flex items-center h-5"
            style={{
              transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? "translateY(-4px)" : "translateY(0)",
            }}
          >
            {messages[activeIndex]}
          </div>
        </div>
      </div>

      {/* 닫기 — 미니멀 */}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="shrink-0 ml-2 flex items-center justify-center"
        style={{
          width: "20px",
          height: "20px",
          borderRadius: "6px",
          color: "#D97706",
          opacity: 0.5,
          background: "none",
          border: "none",
        }}
        aria-label="배너 닫기"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}
